<template>
  <div class="m-slider">
    <div class="m-slider__wrapper">
      <div class="m-slider__label" :class="hasError ? 'm-slider__label--error' : ''">
        {{ alias }}
        <LIconButton v-if="modalContent" class="m-slider__info-button" size="xl" icon="lnx-info" aria-label="info" @click="openModal" />
      </div>
      <LTextfield
        :id="alias"
        type="text"
        inputmode="numeric"
        :class="sliderInputClasses"
        :model-value="inputEditing ? rawInputValue : formattedValue"
        :min="min"
        :max="max"
        :step="1"
        :error="hasError"
        :mask="{
          number: {
            locale: 'de-DE'
          }
        }"
        append-icon="lnx-euro"
        @blur="handleBlur"
        @input="handleInput"
        @focus="handleFocus"
      />
      <div class="m-slider__range-slider">
        <LRangeSlider
          v-model="localValue"
          :min="min"
          :max="max"
          :step="1"
          :infos="infoMessage"
          :error="hasError"
          :errors="error"
          @change="handleSliderChange"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { LIconButton, LRangeSlider, LTextfield } from '@wgg.hyve/hyve-ui-library'
import { computed, ref, watch } from 'vue'
import { extractNumberFromFormattedString, formatNumber } from '@/services/helper.service.ts'

interface MSliderProps {
  alias?: string
  min: number
  max: number
  stepWidth: number
  infoMessage: string
  appendIcon?: string
  modalContent?: string
  error?: string
}

const props = defineProps<MSliderProps>()
const emit = defineEmits(['open-modal'])

const modelValue = defineModel<string | number>({ required: true })

const localValue = ref(modelValue.value)
const inputEditing = ref(false)
const rawInputValue = ref('')
const hasError = computed(() => !!props.error && props.error.length > 0)

const formattedValue = computed(() => formatNumber(Number(localValue.value)))
const sliderInputClasses = computed(() => ({
  'm-slider__input': true,
  'm-slider__input--error': !!props.error?.length
}))

watch(modelValue, (newValue) => {
  localValue.value = newValue
})

// Event-Handler
const handleInput = (event: Event) => {
  inputEditing.value = true
  rawInputValue.value = (event.target as HTMLInputElement).value
}

const handleBlur = () => {
  const parsedValue = extractNumberFromFormattedString(rawInputValue.value)
  const nextValue = rawInputValue.value ? parsedValue : 0
  rawInputValue.value = ''
  inputEditing.value = false

  modelValue.value = isNaN(nextValue) ? 0 : nextValue
}

const handleFocus = (event: FocusEvent) => {
  const input = event.target as HTMLInputElement
  rawInputValue.value = input.value
  inputEditing.value = true
  // Defer selection to avoid immediate blur due to mousedown/focus timing
  requestAnimationFrame(() => input.select())
}

const handleSliderChange = (event: Event) => {
  const value = Number((event.target as HTMLInputElement).value)
  if (!isNaN(value)) modelValue.value = value
}

const openModal = () => {
  if (props.modalContent) emit('open-modal', props.modalContent)
}
</script>

<style lang="scss">
@import 'm-slider';
</style>
